import React, { useMemo, useContext } from 'react';
import classNames from 'classnames';
import {
  DATA_ROOM_NODE_ACCESS_STATES,
  DataRoomNodeWithStateAndSignal,
  Lead,
} from 'models/Lead';
import { Loader } from 'ncoded-component-library';
import SoftSignalIcon from 'components/SoftSignalIcon';
import ResolvePopup from 'components/ResolvePopup';
import { useTranslation } from 'react-i18next';
import LeadsContext from '../../../../providers/Leads';
import useReleaseFileForLead from '../../../../hooks/useReleaseFileForLead';
import ReleaseFileLoadingContext from '../../../../providers/ReleaseFileLoading';
import {
  getAccessLevelIcon,
  getDataRoomNodeAccessDisplayState,
} from '../../../LeadsTableContainer/utils';

import './ReleaseFileInstance.styles.scss';

export const RELEASE_FILE_ACCESS_COPY = {
  [DATA_ROOM_NODE_ACCESS_STATES.NOT_VIEWED]: 'shared',
  [DATA_ROOM_NODE_ACCESS_STATES.NO_ACCESS]: 'noAccess',
  [DATA_ROOM_NODE_ACCESS_STATES.PENDING]: 'pending',
  [DATA_ROOM_NODE_ACCESS_STATES.VIEWED]: 'viewed',
};

type ReleaseFileInstanceProps = {
  className?: string;
  leadId: string;
  disabled?: boolean;
  dataRoomNodeWithStateAndSignal: DataRoomNodeWithStateAndSignal;
  onSuccessRelease?: (lead: Lead) => void;
};

const ReleaseFileInstance: React.FC<ReleaseFileInstanceProps> = (props) => {
  const {
    className,
    leadId,
    dataRoomNodeWithStateAndSignal,
    disabled = false,
    onSuccessRelease,
  } = props;

  const { dataRoomNodeId, softSignal, dataRoomNode } =
    dataRoomNodeWithStateAndSignal;

  const { name = '' } = dataRoomNode || {};

  const AccessLevelIcon = getAccessLevelIcon(dataRoomNodeWithStateAndSignal);

  const displayState = getDataRoomNodeAccessDisplayState(
    dataRoomNodeWithStateAndSignal,
  );

  const { t } = useTranslation();

  const { isReleaseFileLoadingForLead } = useContext(ReleaseFileLoadingContext);
  const { isUndoingReleaseFileForNode } = useContext(LeadsContext);

  const releaseFileForLead = useReleaseFileForLead();

  const isUndoingThisFile = useMemo(
    () => isUndoingReleaseFileForNode(dataRoomNodeId),
    [dataRoomNodeId, isUndoingReleaseFileForNode],
  );

  const isLoading =
    isReleaseFileLoadingForLead(leadId, dataRoomNodeId) || isUndoingThisFile;
  const canRelease = !isLoading && !disabled;

  const classes = classNames(
    'release-file-instance',
    displayState,
    {
      clickable: canRelease,
    },
    className,
  );

  const fileContent = useMemo(
    () => (
      <div
        className={classes}
        {...(canRelease && {
          onClick: (e) => {
            e.stopPropagation();
            releaseFileForLead({
              leadId,
              dataRoomNodeId,
              name,
              onSuccessRelease,
            });
          },
        })}
      >
        {isLoading && <Loader size="small" />}

        <AccessLevelIcon className="access-level" />

        <span className="release-file-instance__name">{name}</span>

        <SoftSignalIcon className="soft-signal" softSignal={softSignal} />
      </div>
    ),
    [
      classes,
      canRelease,
      isLoading,
      name,
      softSignal,
      leadId,
      dataRoomNodeId,
      AccessLevelIcon,
      onSuccessRelease,
      releaseFileForLead,
    ],
  );

  return (
    <ResolvePopup
      onHover
      popupContent={
        <div className="release-file-instance__popup__content">
          {name}
          <p>{t(RELEASE_FILE_ACCESS_COPY[displayState])}</p>
        </div>
      }
      className="release-file-instance__popup"
    >
      {fileContent}
    </ResolvePopup>
  );
};

export default ReleaseFileInstance;
